// src/components/commerce/CommerceOrders.js
// Commandes — /commerce/orders. One canonical order list per account, fed by
// the batch API, Shopify and PrestaShop; every shipment is watched by the
// parcel monitor and a parcel problem surfaces here, as a signed webhook and
// as a note on the merchant's order (Backend docs/commerce-orders.md).
// « À vérifier » gathers what needs a person (parcel problems, monitoring off,
// failing webhooks, a shop whose import fails); the tiles filter the list.
// Tabs and list filters live in the URL (?tab=, ?q=, ?status=, ?source=,
// ?problem=1, ?order=<id>), so a link can open « Webhooks », a search or an
// order directly.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import jwtDecode from 'jwt-decode';
import {
    ChevronRight, CircleAlert, Code2, PackageCheck, Radar, RefreshCw, ShoppingCart, Store, TriangleAlert, Truck, Webhook,
} from 'lucide-react';
import OrdersTab from './OrdersTab';
import ApiTab from './ApiTab';
import WebhooksTab from './WebhooksTab';
import { get } from './api';
import { list as listShops } from '../shops/api';
import { SOURCE_LABEL, commerceAttention } from './commerceAttention';
import { Badge, Banner, Card, Kpi, PageHeader, Tabs } from '../../design/ui';
import './CommerceOrders.css';

// Shops are connected on /shops (one place for orders, parcels and the catalogue).
const TABS = ['orders', 'api', 'webhooks'];
const FILTER_KEYS = ['q', 'source', 'status', 'problem'];

const isAdminToken = () => {
    try {
        return jwtDecode(localStorage.getItem('access_token') || '')?.role !== 'employee';
    } catch (e) {
        return false;
    }
};

const CommerceOrders = () => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language || 'fr';
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const tab = TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'orders';
    const [stats, setStats] = useState(undefined); // undefined = loading, null = unavailable
    const [webhooks, setWebhooks] = useState(null);
    const [shops, setShops] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const isAdmin = useMemo(isAdminToken, []);

    const loadStats = useCallback(async () => {
        try {
            const res = await get('/orders/stats');
            setStats(res.data);
        } catch (e) {
            setStats(null);
        }
    }, []);

    // Read-only context for « À vérifier »: failing webhooks, shops whose import fails.
    const loadContext = useCallback(async () => {
        try {
            const res = await get('/webhooks');
            setWebhooks(res.data?.items || []);
        } catch (e) {
            setWebhooks(null);
        }
        try {
            const res = await listShops();
            setShops(res.data?.connections || []);
        } catch (e) {
            setShops(null);
        }
    }, []);

    useEffect(() => { loadStats(); }, [loadStats, refreshKey]);
    useEffect(() => { loadContext(); }, [loadContext, refreshKey]);

    const filters = {
        q: searchParams.get('q') || '',
        source: searchParams.get('source') || '',
        status: searchParams.get('status') || '',
        problem: searchParams.get('problem') === '1',
    };

    const goTo = (next) => setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (next === 'orders') params.delete('tab'); else params.set('tab', next);
        return params;
    }, { replace: true });

    // A tile or an « À vérifier » line opens the orders list with its filter.
    const setFilters = (patch) => setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.delete('tab');
        Object.entries(patch).forEach(([key, value]) => {
            if (value === '' || value === null || value === false || value === undefined) params.delete(key);
            else params.set(key, value === true ? '1' : String(value));
        });
        return params;
    }, { replace: true });
    // ?order=<id> opens an order's drawer (a link can point at one order).
    const openOrder = (id) => setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (id) params.set('order', id); else params.delete('order');
        return params;
    }, { replace: true });
    const filterOnly = (patch) => setFilters({ ...Object.fromEntries(FILTER_KEYS.map((key) => [key, null])), ...patch });
    const toggleStatus = (value) => filterOnly({ status: filters.status === value && tab === 'orders' ? null : value });

    if (searchParams.get('tab') === 'connections') return <Navigate to="/shops" replace />;

    const monitoring = stats?.parcel_monitoring?.enabled;
    const bySource = stats?.by_source || {};
    const byStatus = stats?.by_status || {};
    const total = Number(stats?.total || 0);
    const count = (value) => Number(value || 0).toLocaleString(lang);
    const share = (part) => (total > 0 ? Math.round((part / total) * 100) : 0);
    const attention = commerceAttention({ stats, webhooks, shops });
    const onOrders = tab === 'orders';

    const attentionText = (item) => {
        switch (item.kind) {
        case 'problems':
            return {
                title: t('ordersMoneyUi.orders.attentionProblems', '{{count}} commandes ont un problème colis en cours', { count: item.count }),
                detail: t('ordersMoneyUi.orders.attentionProblemsHint', 'Un colis est bloqué, silencieux ou signalé par le transporteur : traitez-le, puis marquez-le comme traité sur la commande.'),
                action: () => filterOnly({ problem: true }),
            };
        case 'monitoringOff':
            return {
                title: t('ordersMoneyUi.orders.attentionMonitoring', 'Les colis des commandes ne sont pas surveillés'),
                detail: t('commerceOrders.docsMonitoringOff', 'La surveillance colis est désactivée : les commandes sont enregistrées mais leurs colis ne sont pas suivis. Activez-la dans Surveillance colis.'),
                action: () => navigate('/parcel-monitor'),
            };
        case 'webhookFailing':
            return {
                title: t('ordersMoneyUi.orders.attentionWebhookFailing', 'Le webhook {{host}} échoue', { host: item.host }),
                detail: t('ordersMoneyUi.orders.attentionWebhookFailingHint', '{{count}} envois consécutifs en échec{{error}} : votre serveur ne reçoit plus les problèmes colis.', { count: item.failures, error: item.error ? ` (${item.error})` : '' }),
                action: () => goTo('webhooks'),
            };
        case 'webhookPending':
            return {
                title: t('ordersMoneyUi.orders.attentionWebhookPending', 'Le webhook {{host}} attend son test', { host: item.host }),
                detail: t('commerceOrders.pendingHint', 'Configurez le secret dans votre application, puis cliquez « Tester » : le webhook reçoit les événements dès qu’il répond 2xx.'),
                action: () => goTo('webhooks'),
            };
        case 'shopInstall':
            return {
                title: t('ordersMoneyUi.orders.attentionShopInstall', 'La boutique {{name}} n’est pas installée', { name: item.name }),
                detail: t('ordersMoneyUi.orders.attentionShopInstallHint', 'Ses commandes n’arrivent pas tant que l’application Unanimity n’est pas installée sur Shopify.'),
                action: () => navigate('/shops'),
            };
        default:
            return {
                title: t('ordersMoneyUi.orders.attentionShopImport', 'L’import des commandes de {{name}} échoue', { name: item.name }),
                detail: item.error,
                action: () => navigate('/shops'),
            };
        }
    };

    const sourceLine = ['shopify', 'prestashop', 'api'].filter((s) => bySource[s])
        .map((s) => `${t(`commerceOrders.sources.${s}`, SOURCE_LABEL[s])} ${count(bySource[s])}`).join(' · ');

    return (
        <div className="u-page co-v2">
            <PageHeader
                title={t('commerceOrders.title', 'Commandes')}
                subtitle={stats && total > 0
                    ? t('ordersMoneyUi.orders.subtitle', '{{total}} commandes · {{problems}} avec un problème colis en cours', { count: total, total: count(total), problems: count(stats.problem_open) })
                    : t('commerceOrders.subtitle', 'Vos commandes Shopify, PrestaShop et API au même endroit, avec l’état de chaque colis.')}
                actions={(
                    <>
                        <button type="button" className="u-btn u-btn--secondary u-btn--icon" onClick={() => setRefreshKey((k) => k + 1)}
                            title={t('commerceOrders.refresh', 'Actualiser')} aria-label={t('commerceOrders.refresh', 'Actualiser')}>
                            <RefreshCw aria-hidden="true" />
                        </button>
                        <Link className="u-btn u-btn--secondary" to="/shops">
                            <Store aria-hidden="true" />
                            {t('commerceOrders.shopsLink', 'Boutiques connectées')}
                        </Link>
                    </>
                )}
            />

            <div className="u-well">
                {attention.length > 0 && (
                    <Card
                        icon={TriangleAlert}
                        iconTone="warning"
                        title={t('ordersMoneyUi.attention', 'À vérifier')}
                        badge={<Badge tone="warning">{attention.length}</Badge>}
                        flush
                        className="co-v2__attention"
                    >
                        <ul className="u-list">
                            {attention.map((item) => {
                                const text = attentionText(item);
                                return (
                                    <li className="u-row" key={item.key}>
                                        <Badge tone={item.tone} dot>
                                            {item.tone === 'danger' ? t('ordersMoneyUi.critical', 'Bloquant') : t('ordersMoneyUi.watch', 'À surveiller')}
                                        </Badge>
                                        <div className="u-row__main">
                                            <div className="u-row__title">{text.title}</div>
                                            {text.detail && <div className="u-row__meta u-row__meta--wrap">{text.detail}</div>}
                                        </div>
                                        <div className="u-row__side">
                                            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={text.action}>
                                                {t('ordersMoneyUi.see', 'Voir')}
                                                <ChevronRight aria-hidden="true" />
                                            </button>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </Card>
                )}

                {stats === undefined && (
                    <div className="u-kpi-row co-v2__kpis" aria-hidden="true">
                        {[0, 1, 2, 3, 4, 5].map((i) => <span key={i} className="u-skeleton co-v2__kpi-skeleton" />)}
                    </div>
                )}
                {stats && total === 0 && !monitoring && (
                    <Banner
                        tone="warning"
                        title={t('ordersMoneyUi.orders.attentionMonitoring', 'Les colis des commandes ne sont pas surveillés')}
                        actions={<Link className="u-btn u-btn--secondary u-btn--sm" to="/parcel-monitor">{t('commerceOrders.openMonitorShort', 'Ouvrir la surveillance')}</Link>}
                    >
                        {t('commerceOrders.docsMonitoringOff', 'La surveillance colis est désactivée : les commandes sont enregistrées mais leurs colis ne sont pas suivis. Activez-la dans Surveillance colis.')}
                    </Banner>
                )}
                {stats && total > 0 && (
                    <div className="u-kpi-row co-v2__kpis">
                        <Kpi
                            icon={ShoppingCart}
                            label={t('ordersMoneyUi.orders.kpiOrders', 'Commandes')}
                            value={count(total)}
                            context={sourceLine || t('ordersMoneyUi.orders.kpiOrdersNone', 'aucune commande reçue')}
                            active={onOrders && !filters.status && !filters.problem && !filters.source && !filters.q}
                            onClick={() => filterOnly({})}
                        />
                        <Kpi
                            icon={CircleAlert}
                            tone={stats.problem_open ? 'danger' : 'success'}
                            label={t('ordersMoneyUi.orders.kpiProblems', 'Problèmes colis')}
                            value={count(stats.problem_open)}
                            context={stats.problem_open
                                ? t('ordersMoneyUi.orders.kpiProblemsContext', 'commandes à traiter · {{share}} %', { share: share(stats.problem_open) })
                                : t('ordersMoneyUi.orders.kpiProblemsNone', 'aucun colis en difficulté')}
                            contextTone={stats.problem_open ? 'danger' : undefined}
                            active={onOrders && filters.problem}
                            onClick={() => filterOnly({ problem: !(onOrders && filters.problem) })}
                        />
                        <Kpi
                            icon={Truck}
                            tone="info"
                            label={t('ordersMoneyUi.orders.kpiShipped', 'En transit')}
                            value={count(byStatus.shipped)}
                            context={t('ordersMoneyUi.orders.kpiShippedContext', 'expédiées, pas encore livrées')}
                            active={onOrders && filters.status === 'shipped'}
                            onClick={() => toggleStatus('shipped')}
                        />
                        <Kpi
                            icon={PackageCheck}
                            tone="success"
                            label={t('ordersMoneyUi.orders.kpiDelivered', 'Livrées')}
                            value={count(byStatus.delivered)}
                            context={t('ordersMoneyUi.orders.kpiShare', '{{share}} % des commandes', { share: share(Number(byStatus.delivered || 0)) })}
                            meter={total > 0 ? { value: Number(byStatus.delivered || 0), max: total, tone: 'success', label: t('ordersMoneyUi.orders.kpiDelivered', 'Livrées') } : undefined}
                            active={onOrders && filters.status === 'delivered'}
                            onClick={() => toggleStatus('delivered')}
                        />
                        <Kpi
                            icon={TriangleAlert}
                            tone="warning"
                            label={t('ordersMoneyUi.orders.kpiIncidents', 'Incidents')}
                            value={count(byStatus.incident)}
                            context={t('ordersMoneyUi.orders.kpiIncidentsContext', 'statut de la commande · {{returned}} retournées', { returned: count(byStatus.returned) })}
                            active={onOrders && filters.status === 'incident'}
                            onClick={() => toggleStatus('incident')}
                        />
                        <Kpi
                            icon={Radar}
                            tone={monitoring ? 'success' : 'warning'}
                            label={t('ordersMoneyUi.orders.kpiMonitoring', 'Surveillance des colis')}
                            value={monitoring ? t('commerceOrders.monitoringOn', 'Active') : t('commerceOrders.monitoringOff', 'Désactivée')}
                            context={monitoring
                                ? t('commerceOrders.openMonitorShort', 'Ouvrir la surveillance')
                                : t('commerceOrders.enableMonitoring', 'Activer pour suivre les colis des commandes')}
                            contextTone={monitoring ? undefined : 'warning'}
                            onClick={() => navigate('/parcel-monitor')}
                        />
                    </div>
                )}

                <Tabs
                    label={t('commerceOrders.title', 'Commandes')}
                    active={tab}
                    onChange={goTo}
                    tabs={[
                        { key: 'orders', label: t('commerceOrders.tabs.orders', 'Commandes'), icon: ShoppingCart, count: stats ? count(total) : undefined },
                        { key: 'api', label: t('commerceOrders.tabs.api', 'API'), icon: Code2 },
                        { key: 'webhooks', label: t('commerceOrders.tabs.webhooks', 'Webhooks'), icon: Webhook, count: webhooks ? webhooks.length : undefined },
                    ]}
                />

                <div role="tabpanel">
                    {tab === 'orders' && (
                        <OrdersTab
                            filters={filters}
                            onFilter={setFilters}
                            onGoTo={goTo}
                            refreshKey={refreshKey}
                            onChanged={loadStats}
                            openId={searchParams.get('order')}
                            onOpen={openOrder}
                        />
                    )}
                    {tab === 'api' && <ApiTab isAdmin={isAdmin} monitoring={monitoring} />}
                    {tab === 'webhooks' && <WebhooksTab isAdmin={isAdmin} onChanged={loadContext} />}
                </div>
            </div>
        </div>
    );
};

export default CommerceOrders;
